import type { EditorLayoutHint } from "@loumy/contracts";
import { EditorRegion } from "@loumy/contracts";
import { create } from "zustand";

// D66 Editor-UI-State (Client-only): aktiver Editor-Typ, je-Editor das Dokument (TDoc) und
// der ephemere UI-State (TUi), die Selektion und das Panel-Layout je Editor. NUR das Layout
// wird in localStorage persistiert — Dokumente + UI-State NICHT (D51/D47: kein paralleler
// Daten-Pfad; der saubere Persistenz-Weg ist Asset/Job, nicht der Browser-Store). Server-
// State liegt getrennt in TanStack Query (state/queries).

export interface RegionLayout {
  /** px — Breite für tools/outliner/details, Höhe für toolbar; der Viewport füllt den Rest. */
  size: number;
  collapsed: boolean;
}
export type EditorLayout = Record<EditorRegion, RegionLayout>;

const REGIONS = EditorRegion.options;

/** Framework-Defaults, falls ein Editor keine layoutHints liefert. */
export const DEFAULT_LAYOUT: EditorLayout = {
  toolbar: { size: 48, collapsed: false },
  tools: { size: 200, collapsed: false },
  viewport: { size: 0, collapsed: false }, // Rest-Fläche → size ungenutzt
  outliner: { size: 280, collapsed: false },
  details: { size: 260, collapsed: false },
};

/** Start-Layout eines Editors aus seinen layoutHints (Default-Größen) + Framework-Defaults. */
export function layoutFromHints(hints?: Partial<Record<EditorRegion, EditorLayoutHint>>): EditorLayout {
  const out = {} as EditorLayout;
  for (const region of REGIONS) {
    out[region] = { size: hints?.[region]?.defaultSize ?? DEFAULT_LAYOUT[region].size, collapsed: false };
  }
  return out;
}

const LAYOUT_KEY = "loumy.editor.layouts";
const MAX_SIZE = 2000;

const clampSize = (n: number): number => (Number.isFinite(n) ? Math.max(0, Math.min(MAX_SIZE, Math.round(n))) : 0);

function isRegionLayout(v: unknown): v is RegionLayout {
  return (
    typeof v === "object" &&
    v !== null &&
    typeof (v as { size?: unknown }).size === "number" &&
    typeof (v as { collapsed?: unknown }).collapsed === "boolean"
  );
}

// Persistierte Layouts defensiv laden: korruptes JSON → {}; je Editor nur VALIDE Regionen
// übernehmen (Größen geclampt) — invalide/fehlende Regionen ergänzt `initEditor` aus den
// Editor-Hints. Schützt gegen manipuliertes localStorage und Schema-Updates.
function loadLayouts(): Record<string, EditorLayout> {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(LAYOUT_KEY) : null;
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: Record<string, EditorLayout> = {};
    for (const [editorId, layout] of Object.entries(parsed as Record<string, unknown>)) {
      if (!layout || typeof layout !== "object") continue;
      const clean: Partial<EditorLayout> = {};
      for (const r of REGIONS) {
        const rl = (layout as Record<string, unknown>)[r];
        if (isRegionLayout(rl)) clean[r] = { size: clampSize(rl.size), collapsed: rl.collapsed };
      }
      if (Object.keys(clean).length > 0) out[editorId] = clean as EditorLayout;
    }
    return out;
  } catch {
    return {};
  }
}

function persistLayouts(layouts: Record<string, EditorLayout>): void {
  try {
    localStorage.setItem(LAYOUT_KEY, JSON.stringify(layouts));
  } catch {
    /* private mode */
  }
}

interface EditorState {
  activeEditorType: string | null;
  /** Editor-Wechsel setzt die Selektion zurück (eine sichtbare Editor-Instanz, §11). */
  setActiveEditor: (id: string | null) => void;

  docs: Record<string, unknown>;
  setDoc: (editorId: string, doc: unknown) => void;

  uiStates: Record<string, unknown>;
  setUi: (editorId: string, ui: unknown) => void;

  selection: string | null;
  setSelection: (id: string | null) => void;

  layouts: Record<string, EditorLayout>;
  resizeRegion: (editorId: string, region: EditorRegion, size: number) => void;
  /** Persistiert das aktuelle Layout (Shell ruft es am Drag-Ende, nicht je pointermove). */
  commitLayout: () => void;
  toggleRegion: (editorId: string, region: EditorRegion) => void;

  /**
   * Idempotente Erst-Initialisierung (Shell beim Editor-Mount): legt doc/ui/layout NUR an,
   * falls noch nicht vorhanden — überschreibt bestehenden State nie (analog SettingsPanel/
   * defaultsOf). Persistiertes (evtl. unvollständiges) Layout wird mit fehlenden Regionen
   * aus `init.layout` ergänzt.
   */
  initEditor: (editorId: string, init: { doc: unknown; ui: unknown; layout: EditorLayout }) => void;
}

export const useEditorStore = create<EditorState>((set, get) => ({
  activeEditorType: null,
  setActiveEditor: (id) => set((s) => (s.activeEditorType === id ? s : { activeEditorType: id, selection: null })),

  docs: {},
  setDoc: (editorId, doc) => set((s) => ({ docs: { ...s.docs, [editorId]: doc } })),

  uiStates: {},
  setUi: (editorId, ui) => set((s) => ({ uiStates: { ...s.uiStates, [editorId]: ui } })),

  selection: null,
  setSelection: (id) => set({ selection: id }),

  layouts: loadLayouts(),
  // Live-Resize: NUR Store (kein localStorage-Write je pointermove → kein Main-Thread-Jank);
  // die Shell committet am Drag-Ende via commitLayout.
  resizeRegion: (editorId, region, size) =>
    set((s) => {
      const current = s.layouts[editorId] ?? DEFAULT_LAYOUT;
      const region0 = current[region] ?? DEFAULT_LAYOUT[region];
      return {
        layouts: { ...s.layouts, [editorId]: { ...current, [region]: { ...region0, size: clampSize(size) } } },
      };
    }),
  commitLayout: () => persistLayouts(get().layouts),
  toggleRegion: (editorId, region) =>
    set((s) => {
      const current = s.layouts[editorId] ?? DEFAULT_LAYOUT;
      const region0 = current[region] ?? DEFAULT_LAYOUT[region];
      const next = {
        ...s.layouts,
        [editorId]: { ...current, [region]: { ...region0, collapsed: !region0.collapsed } },
      };
      persistLayouts(next);
      return { layouts: next };
    }),

  initEditor: (editorId, init) =>
    set((s) => {
      const patch: Partial<EditorState> = {};
      if (!(editorId in s.docs)) patch.docs = { ...s.docs, [editorId]: init.doc };
      if (!(editorId in s.uiStates)) patch.uiStates = { ...s.uiStates, [editorId]: init.ui };
      const existing = s.layouts[editorId];
      // Fehlende/invalide Regionen (z.B. nach Framework-Update oder partiell geladenem
      // localStorage) defensiv aus den Hints ergänzen — `init.layout` zuerst, `existing`
      // gewinnt, wo vorhanden. Reparatur wird SOFORT zurückgeschrieben (sonst wiederholt
      // sie sich bei jedem Reload).
      const needsRepair = !existing || REGIONS.some((r) => existing[r] === undefined);
      if (needsRepair) {
        const next = { ...s.layouts, [editorId]: { ...init.layout, ...(existing ?? {}) } };
        persistLayouts(next);
        patch.layouts = next;
      }
      return patch;
    }),
}));
